import React, { useEffect, useState } from "react";
import { FormikProvider, useFormik, Field, Form, ErrorMessage } from "formik";
import * as jnb from "react-bootstrap";
import * as Yup from "yup";
import { config } from "../CommonUtils/CommonURLS";
import allowNumbersOnly, { allowAlphabetAndSpacesOnly } from "../CommonUtils/CommonValidations";
import { emailValidation, ERROR_MSG, mobileValidation } from "../CommonUtils/contextVariables";
import { CommonAxiosGet, CommonAxiosPost } from "../CommonUtils/CommonAxios";
import { successAlert2, failureAlert } from "../CommonUtils/SweetAlerts";

export default function RejectNoc({ selectedStudent, setShowCheckModal, fetchStudents, setSubmitting, fromToCollege }) {
  const [loading, setLoading] = useState(false);
  const [apiNocRejectDetails, setApiNocRejectDetails] = useState({});
  const [studentProfile, setStudentProfile] = useState({
    student_reg_no: "",
    student_name: "",
    branch_name: ""
  });

  // ✅ Fetch student profile using API
  async function getStudentProfile() {
    const studentId = selectedStudent?.student_reg_no;
    if (!studentId) return false;
    const url = config.url.STU_URL + "getStudentProfile/" + studentId;
    try {
      const res = await CommonAxiosGet(url);
      const profileList = res?.data?.data || [];

      if (res?.data?.scode === "01" && Array.isArray(profileList) && profileList.length > 0) {
        const profile = profileList[0];
        setStudentProfile({
          student_reg_no: profile.student_reg_no || "",
          student_name: profile.student_name || "",
          branch_name: profile.branch_name || ""
        });
        return true;
      }
    } catch (error) {
      console.error("Error fetching student profile:", error);
    }
    return false;
  }

  const initialValues = {
    studentRegNo: selectedStudent?.student_reg_no || "",
    candidateName: studentProfile.student_name || "",
    branch: studentProfile.branch_name || "",
    principalName: apiNocRejectDetails?.principal_name || "",
    principalMobileNo: apiNocRejectDetails?.principal_mobile_no || "",
    principalEmailId: apiNocRejectDetails?.principal_email_id || "",
    nocRemarks: apiNocRejectDetails?.noc_remarks || "",
  };

  const validationSchema = Yup.object().shape({
    studentRegNo: Yup.string().required(ERROR_MSG),
    candidateName: Yup.string().required(ERROR_MSG),
    branch: Yup.string().required(ERROR_MSG),
    principalName: Yup.string().required(ERROR_MSG),
    principalMobileNo: mobileValidation,
    principalEmailId: emailValidation,
    nocRemarks: Yup.string().required("Reason for rejection is required"),
  });

  const rejectFormik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: handleRejectSubmit,
  });

  async function handleRejectSubmit(values) {
    setSubmitting(true);
    setLoading(true);
    try {
      const payload = {
        fromToCollege: fromToCollege,
        studentRegNo: values.studentRegNo,
        studentName: values.candidateName,
        branch: values.branch,
        principalName: values.principalName,
        principalMobileNo: values.principalMobileNo,
        principalEmailId: values.principalEmailId,
        nocRemarks: values.nocRemarks,
        nocStatus: "Rejected",
      };

      const res = await CommonAxiosPost(`${config.url.STU_URL}saveStudentnoc`, payload);
      if (res?.data?.status === true || res?.data?.scode === "01") {
        successAlert2(res?.data?.message || "NOC Rejection submitted successfully");
        setShowCheckModal(false);
        fetchStudents();
      } else {
        failureAlert(res?.data?.message || "Failed to submit rejection");
      }
    } catch (error) {
      console.error("Error submitting rejection:", error);
      failureAlert("Error submitting rejection");
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  }

  // ✅ Get API to fetch NOC details
  function GetNocDetails() {
    if (!selectedStudent?.student_reg_no) return;
    let url = `${config.url.STU_URL}getStudentnoc_dtls/${selectedStudent?.student_reg_no}/${fromToCollege}`;
    CommonAxiosGet(url)
      .then((response) => {
        if (response?.data?.status === true) {
          const data = Array.isArray(response?.data?.data) ? response?.data?.data[0] : response?.data?.data;
          setApiNocRejectDetails(data || {});
        } else {
          setApiNocRejectDetails({});
        }
      })
      .catch((error) => {
        console.error("Error fetching NOC details:", error);
        setApiNocRejectDetails({});
      });
  }

  useEffect(() => {
    if (selectedStudent?.student_reg_no) {
      getStudentProfile();
      GetNocDetails();
    }
  }, [selectedStudent?.student_reg_no]);

  const isRejectionAlreadySubmitted = apiNocRejectDetails && Object.keys(apiNocRejectDetails).length > 0;
  return (
    <jnb.Container fluid className="st-form-page py-3">
          <FormikProvider value={rejectFormik}>
            <Form onSubmit={rejectFormik.handleSubmit} id="reject-noc-form">
              {/* Student Basic Details Section */}
              <section className="st-form-card">
                <div className="st-form-card-header">
                  <span className="st-form-card-index">1</span>
                  <h5>Student Basic Details</h5>
                </div>
                <jnb.Row className="g-3 g-lg-4 p-3 p-lg-4">
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Reg. No / Admission No <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field name="studentRegNo" className="form-control st-control" disabled />
                    <ErrorMessage name="studentRegNo" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Name of the candidate <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field name="candidateName" className="form-control st-control" disabled />
                    <ErrorMessage name="candidateName" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Branch <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field name="branch" className="form-control st-control" disabled />
                    <ErrorMessage name="branch" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>
                </jnb.Row>
              </section>

              {/* Principal Details Section */}
              <section className="st-form-card">
                <div className="st-form-card-header">
                  <span className="st-form-card-index">2</span>
                  <h5>Principal Details</h5>
                </div>
                <jnb.Row className="g-3 g-lg-4 p-3 p-lg-4">
                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Name of the Principal <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field name="principalName" className="form-control st-control" maxLength={100} onKeyPress={allowAlphabetAndSpacesOnly} disabled={isRejectionAlreadySubmitted} />
                    <ErrorMessage name="principalName" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Principal Mobile No <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field name="principalMobileNo" className="form-control st-control" maxLength={10} onKeyPress={allowNumbersOnly} disabled={isRejectionAlreadySubmitted} />
                    <ErrorMessage name="principalMobileNo" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>

                  <jnb.Col xs={12} md={6} lg={4}>
                    <jnb.Form.Label className="st-label">
                      Principal Email ID <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field name="principalEmailId" type="email" className="form-control st-control" maxLength={100} disabled={isRejectionAlreadySubmitted} />
                    <ErrorMessage name="principalEmailId" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>
                </jnb.Row>
              </section>

              {/* Rejection Reason Section */}
              <section className="st-form-card">
                <div className="st-form-card-header">
                  <span className="st-form-card-index">3</span>
                  <h5>Reason For Rejection</h5>
                </div>
                <jnb.Row className="g-3 g-lg-4 p-3 p-lg-4">
                  <jnb.Col xs={12}>
                    <jnb.Form.Label className="st-label">
                      Enter Reason For Rejection <span className="text-danger">*</span>
                    </jnb.Form.Label>
                    <Field
                      as="textarea"
                      name="nocRemarks"
                      className="form-control st-control"
                      rows={5}
                      placeholder="Please provide detailed reason for rejection..."
                      disabled={isRejectionAlreadySubmitted}
                    />
                    <ErrorMessage name="nocRemarks" component="div" className="text-danger extra-small mt-1" />
                  </jnb.Col>
                </jnb.Row>
              </section>

              {/* Submit Buttons */}
              <jnb.Row className="g-3 mt-4 mb-2 p-3 p-lg-4">
                <jnb.Col className="d-flex gap-2 justify-content-center">
                  {isRejectionAlreadySubmitted ? (
                    <span className="fw-bold text-success">✓ Rejection Already Submitted</span>
                  ) : (
                    <>
                      <jnb.Button variant="secondary" type="button" disabled={loading} onClick={() => setShowCheckModal(false)}>
                        Cancel
                      </jnb.Button>

                      <jnb.Button variant="danger" type="submit" disabled={loading}>
                        {loading ? "Submitting..." : "Submit Rejection"}
                      </jnb.Button>
                    </>
                  )}
                </jnb.Col>
              </jnb.Row>
            </Form>
          </FormikProvider>
    </jnb.Container>
  );
}